import { INK } from './palette.js';

/**
 * The five check-in faces.
 *
 * Every one of them is the SAME colour. The obvious design - green for happy
 * through amber to red for sad - is a traffic light, and a traffic light tells
 * a child that some answers are correct and others are a problem. That is the
 * fastest way to teach a five-year-old to tap the green one regardless of how
 * the day actually went, which would make the whole feature worthless.
 *
 * The low faces are drawn as genuinely sad, not pathetic, weeping or ashamed.
 * A child should be able to point at one without it feeling like an admission.
 */

const FACE = '#f2c879';
const FACE_SHADE = '#e0b05c';

interface MoodFaceProps {
  mood: string;
  size?: number;
  title?: string;
}

export function MoodFace({ mood, size = 96, title }: MoodFaceProps) {
  return (
    <svg viewBox="0 0 100 100" width={size} height={size} role="img" aria-label={title ?? mood}>
      <circle cx="50" cy="50" r="42" fill={FACE} stroke={FACE_SHADE} strokeWidth="3" />
      {EYES[mood] ?? EYES.ok}
      {MOUTH[mood] ?? MOUTH.ok}
    </svg>
  );
}

const openEyes = (
  <>
    <circle cx="36" cy="43" r="4.5" fill={INK} />
    <circle cx="64" cy="43" r="4.5" fill={INK} />
  </>
);

const EYES: Record<string, React.ReactElement> = {
  // Crescents: the eyes of somebody actually pleased, not just a smile drawn on.
  great: (
    <>
      <path d="M30 45 q6 -8 12 0" fill="none" stroke={INK} strokeWidth="4" strokeLinecap="round" />
      <path d="M58 45 q6 -8 12 0" fill="none" stroke={INK} strokeWidth="4" strokeLinecap="round" />
    </>
  ),
  good: openEyes,
  ok: openEyes,
  low: openEyes,
  // Slightly lowered lids. Tired and flat, which is what a rough day looks
  // like at five - not tears.
  rough: (
    <>
      <circle cx="36" cy="45" r="4.5" fill={INK} />
      <circle cx="64" cy="45" r="4.5" fill={INK} />
      <path d="M30 38 q6 -3 12 0" fill="none" stroke={INK} strokeWidth="3" strokeLinecap="round" />
      <path d="M58 38 q6 -3 12 0" fill="none" stroke={INK} strokeWidth="3" strokeLinecap="round" />
    </>
  ),
};

const MOUTH: Record<string, React.ReactElement> = {
  great: (
    <path
      d="M32 62 q18 20 36 0 q-18 8 -36 0"
      fill={INK}
      stroke={INK}
      strokeWidth="3"
      strokeLinejoin="round"
    />
  ),
  good: (
    <path d="M34 63 q16 14 32 0" fill="none" stroke={INK} strokeWidth="4" strokeLinecap="round" />
  ),
  // Straight. Neither good nor bad, and drawn so it reads that way.
  ok: <path d="M36 67 h28" fill="none" stroke={INK} strokeWidth="4" strokeLinecap="round" />,
  low: (
    <path d="M36 70 q14 -9 28 0" fill="none" stroke={INK} strokeWidth="4" strokeLinecap="round" />
  ),
  rough: (
    <path d="M34 73 q16 -14 32 0" fill="none" stroke={INK} strokeWidth="4" strokeLinecap="round" />
  ),
};
